/**
 * Better Auth client — SPA side.
 *
 * Uses same-origin baseURL (/api/auth) so cookies are first-party — no CORS issues.
 * `cloudflareClient()` plugin adds geolocation helpers on the client side
 * (mirrors the server-side `cloudflare()` plugin injected by `withCloudflare`).
 *
 * Usage:
 *   import { authClient } from '@/client/lib/auth-client';
 *   const session = await authClient.getSession();
 *   await authClient.signIn.email({ email, password });
 *   await authClient.signOut();
 */

import { createAuthClient } from 'better-auth/react';
import { cloudflareClient } from 'better-auth-cloudflare/client';

export const authClient = createAuthClient({
  // Same-origin: SPA and API share the same Worker → no cross-origin cookie issues.
  // better-auth requires an absolute baseURL (it runs `new URL()`); build it from
  // the current origin so it works on localhost and the production domain alike.
  baseURL: `${window.location.origin}/api/auth`,
  plugins: [
    // Adds geolocation helpers and Cloudflare-aware session handling
    cloudflareClient(),
  ],
});

// Re-export typed hooks for convenience
export const {
  useSession,
  signIn,
  signOut,
  signUp,
  resetPassword,
  sendVerificationEmail,
} = authClient;

/** Type of the authenticated user from a session, with the `role` additional field. */
export type SessionUser = (typeof authClient.$Infer.Session)['user'] & {
  role: string;
};
